import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, Trash2, Search, Download, Upload, FileText, ShoppingCart, Eye, CreditCard, Paperclip, Printer, BarChart2 } from 'lucide-react';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ── Constants ──────────────────────────────────────────────
const CATEGORIES  = ['B2B', 'B2C'];
const DOC_TYPES   = ['Invoice', 'Credit Note', 'Debit Note'];   // Credit Note reduces tax/ITC, Debit Note adds
const TAX_RATES   = [0.05, 0.12, 0.18, 0.28];
const GST_REMARKS = ['GST Filed', 'GST Not Filed', 'GST Pending'];
const ITC_STATUS  = ['Eligible', 'Ineligible', 'Pending'];
const SUPPLY_TYPES = [['Intra', 'Intra-state (CGST + SGST)'], ['Inter', 'Inter-state (IGST)']];
const HOME_STATE_CODE = '32';   // Kerala — a GST number starting with this is an intra-state supply
const PAYMENT_TYPES_LIST = ['Bank Transfer','Cash','Cheque','UPI'];
const PAYMENT_MODES_LIST = ['EKM FDO','EKM Admin','TVM FDO','TVM Admin','Cash','HDFC Vazhakkala','HDFC Kottarakkara','SBI','Ujjivan'];
const PAYMENT_CATS_LIST  = ['Tuition Fees','Hostel Fees','Exam Fees','Manpower Fees','Registration Fees','Salary Recovery','Other Income','Salary','Salary Advance','TA (Travel Allowance)','Food Expenses','Marketing','Rent','Utilities','Office Expenses','Miscellaneous'];
const BUCKET = 'gst-bills';
const MAX_FILE_MB = 5;
// Shown on printed sale invoices — fill in your company details here
const COMPANY = { name: 'Armstech Engineers Pvt Ltd', gstin: '', address: '', phone: '', email: '' };

const KINDS = {
  sale:     { key:'sale',     table:'gst_sale_bills',     source:'gst_sale',     partyKey:'party_name',    partyLabel:'Party',    partyWord:'Customer / party', title:'GST Sale Bills',     cashType:'Cash In',  defCat:'Other Income',    acctLabel:'GST Sale',     itc:false, perm:'gst_sales' },
  purchase: { key:'purchase', table:'gst_purchase_bills', source:'gst_purchase', partyKey:'supplier_name', partyLabel:'Supplier', partyWord:'Supplier / vendor', title:'GST Purchase Bills', cashType:'Cash Out', defCat:'Office Expenses', acctLabel:'GST Purchase', itc:true,  perm:'gst_purchase' },
};

// PostgREST returns max 1000 rows per request — page through everything
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}

// ── Helpers ────────────────────────────────────────────────
const pad2 = n => String(n).padStart(2, '0');
const r2 = n => Math.round((Number(n) + Number.EPSILON) * 100) / 100;   // GST amounts are kept to 2 decimals

const INR = v => Number(v||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; };
const msgFor = e => { const m = (e && e.message) || String(e); return /column|relation|does not exist|schema cache/i.test(m) ? m + ' — run gst_upgrade.sql in Supabase first' : m; };

// Financial year (Apr–Mar). fy = starting year as string, e.g. '2026' = FY 2026-27
const currentFY = () => { const d = new Date(); return String(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1); };
const fyLabel = y => `${y}-${String(Number(y) + 1).slice(2)}`;
const fyRange = fy => ({ from: `${fy}-04-01`, to: `${Number(fy) + 1}-03-31` });
const fyOptions = () => { const a = []; for (let y = 2021; y <= Number(currentFY()) + 1; y++) a.push(String(y)); return a.reverse(); };
const fyMonths = fy => Array.from({ length: 12 }, (_, i) => { const m = (i + 3) % 12 + 1; const y = m >= 4 ? Number(fy) : Number(fy) + 1; return `${y}-${pad2(m)}`; });
const monthLabel = ym => { const [y, m] = ym.split('-'); return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m) - 1] + ' ' + y; };

// GST number: format + checksum
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
function gstinValid(g) {
  g = String(g || '').trim().toUpperCase();
  if (!GSTIN_RE.test(g)) return false;
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const p = chars.indexOf(g[i]) * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(p / 36) + (p % 36);
  }
  return chars[(36 - (sum % 36)) % 36] === g[14];
}
const supplyFromGstin = g => (String(g || '').length === 15 ? (String(g).slice(0, 2) === HOME_STATE_CODE ? 'Intra' : 'Inter') : null);

// Credit notes reduce, invoices & debit notes add
const signOf = r => (r && r.doc_type === 'Credit Note' ? -1 : 1);
// CGST / SGST / IGST split (falls back for older bills saved before the split existed)
function splitTax(taxAmt, supply) {
  const t = r2(taxAmt);
  if (supply === 'Inter') return { cgst: 0, sgst: 0, igst: t };
  const c = r2(t / 2);
  return { cgst: c, sgst: r2(t - c), igst: 0 };
}
function splitOf(r) {
  if (r.cgst_amount != null || r.sgst_amount != null || r.igst_amount != null)
    return { cgst: Number(r.cgst_amount || 0), sgst: Number(r.sgst_amount || 0), igst: Number(r.igst_amount || 0) };
  const sup = r.supply_type || supplyFromGstin(r.gst_number) || 'Intra';
  return splitTax(r.tax_amount || 0, sup);
}
function billStatus(r) {
  if (r.doc_type === 'Credit Note') return { label: 'Credit Note', cls: 'badge-gray', balance: 0 };
  const inv = Number(r.invoice_value || 0), paid = Number(r.paid_amount || 0);
  const balance = r2(inv - paid);
  if (inv > 0 && balance <= 0.01) return { label: 'Paid', cls: 'badge-green', balance: 0 };
  if (paid > 0) return { label: 'Part paid', cls: 'badge-amber', balance };
  return { label: 'Unpaid', cls: 'badge-red', balance };
}
const itcOf = r => r.itc_status || 'Eligible';

// Indian-style amount in words (for printed invoices)
function amountInWords(n) {
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const two = x => x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? ' ' + a[x % 10] : '');
  const three = x => (x >= 100 ? a[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' : '') : '') + (x % 100 ? two(x % 100) : '');
  const rupees = Math.floor(Math.abs(n)), paise = Math.round((Math.abs(n) - rupees) * 100);
  if (rupees === 0 && paise === 0) return 'Zero Rupees Only';
  const parts = [];
  const cr = Math.floor(rupees / 10000000), lk = Math.floor((rupees % 10000000) / 100000), th = Math.floor((rupees % 100000) / 1000), rest = rupees % 1000;
  if (cr) parts.push(three(cr) + ' Crore'); if (lk) parts.push(two(lk) + ' Lakh'); if (th) parts.push(two(th) + ' Thousand'); if (rest) parts.push(three(rest));
  let s = (parts.join(' ') || 'Zero') + ' Rupees';
  if (paise) s += ' and ' + two(paise) + ' Paise';
  return s + ' Only';
}

// Excel/CSV cell -> 'YYYY-MM-DD' (local, no UTC shift). Handles Date, serial number, ISO and dd/mm/yyyy text.
function parseXlDate(v) {
  if (v === '' || v == null) return null;
  if (v instanceof Date) { if (isNaN(v)) return null; const d = new Date(v.getTime() + 12 * 3600 * 1000); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
  if (typeof v === 'number') { if (v < 20000) return null; const d = new Date(Math.round((v - 25569) * 86400 * 1000)); return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`; }
  const t = String(v).trim();
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  m = t.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) { const y = m[3].length === 2 ? '20' + m[3] : m[3]; return `${y}-${pad2(m[2])}-${pad2(m[1])}`; }
  return null;
}

// ── Accounts sync (one Accounts entry per payment) ─────────
async function removePaymentFromAccounts(K, bill, pay) {
  if (pay.acct_ref) await supabase.from('accounts_transactions').delete().eq('source', K.source).eq('reference', pay.acct_ref);
  // very old entries were matched by description only
  if (bill && pay.acct_ref && !/_p\d+$/.test(pay.acct_ref))
    await supabase.from('accounts_transactions').delete().eq('source', K.source).eq('description', `${K.acctLabel} — ${bill[K.partyKey]} (${bill.invoice_number})`);
}
async function syncPaymentToAccounts(K, bill, pay) {
  if (!pay.acct_ref) return null;
  await supabase.from('accounts_transactions').delete().eq('source', K.source).eq('reference', pay.acct_ref);
  if (pay.add_to_accounts !== 'Y') return null;
  const { error } = await supabase.from('accounts_transactions').insert({
    company: 'ARMSTECH', date: pay.paid_date, type: K.cashType,
    category: pay.pay_category || K.defCat, payment_mode: pay.payment_mode || 'Cash',
    amount: Number(pay.paid_amount), reference: pay.acct_ref,
    description: `${K.acctLabel} — ${bill[K.partyKey]} (${bill.invoice_number})${pay.receipt_no ? ' — Receipt ' + pay.receipt_no : ''}`,
    source: K.source,
  });
  return error;
}
// keep bills.paid_amount / paid_date = totals of its payments
async function refreshBillPaid(K, billId) {
  const { data } = await supabase.from('gst_bill_payments').select('paid_amount,paid_date').eq('bill_type', K.key).eq('bill_id', String(billId));
  const list = data || [];
  const total = r2(list.reduce((s, p) => s + Number(p.paid_amount || 0), 0));
  const last = list.map(p => p.paid_date).filter(Boolean).sort().pop() || null;
  await supabase.from(K.table).update({ paid_amount: total || null, paid_date: last }).eq('id', billId);
}
// delete bills completely: payments, their Accounts entries, attachment
async function deleteBillsFully(K, bills) {
  for (const b of bills) {
    const { data: pays } = await supabase.from('gst_bill_payments').select('*').eq('bill_type', K.key).eq('bill_id', String(b.id));
    for (const p of (pays || [])) await removePaymentFromAccounts(K, b, p);
    await supabase.from('gst_bill_payments').delete().eq('bill_type', K.key).eq('bill_id', String(b.id));
    // very old single-payment Accounts entries
    await supabase.from('accounts_transactions').delete().eq('source', K.source).eq('reference', `${K.source}_${b.id}`);
    if (b.attachment_path) await supabase.storage.from(BUCKET).remove([b.attachment_path]);
    const { error } = await supabase.from(K.table).delete().eq('id', b.id);
    if (error) throw error;
  }
}
async function uploadBillFile(K, file) {
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
  const path = `${K.key}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || undefined });
  if (error) throw error;
  return path;
}
async function openBillFile(path) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
  if (error) { toast.error('Could not open file: ' + error.message); return; }
  window.open(data.signedUrl, '_blank');
}

// ── Printable tax invoice (sale bills) ─────────────────────
function printInvoice(r) {
  const sp = splitOf(r), isCN = r.doc_type === 'Credit Note', title = isCN ? 'CREDIT NOTE' : r.doc_type === 'Debit Note' ? 'DEBIT NOTE' : 'TAX INVOICE';
  const rate = (Number(r.tax_rate || 0) * 100).toFixed(0);
  const row = (l, v) => `<tr><td>${l}</td><td style="text-align:right">${v}</td></tr>`;
  const esc = s => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} ${esc(r.invoice_number)}</title>
<style>body{font-family:Arial,sans-serif;font-size:13px;color:#111;margin:30px}h1{font-size:20px;margin:0}table{border-collapse:collapse;width:100%}td,th{border:1px solid #999;padding:6px 8px}
.no{border:none}.hd{display:flex;justify-content:space-between;margin-bottom:14px}.c{text-align:center;font-size:18px;font-weight:700;margin:10px 0}.sm{font-size:11px;color:#444}</style></head><body>
<div class="hd"><div><h1>${esc(COMPANY.name)}</h1>${COMPANY.address ? `<div class="sm">${esc(COMPANY.address)}</div>` : ''}${COMPANY.gstin ? `<div class="sm">GSTIN: ${esc(COMPANY.gstin)}</div>` : ''}${COMPANY.phone || COMPANY.email ? `<div class="sm">${esc(COMPANY.phone)} ${esc(COMPANY.email)}</div>` : ''}</div>
<div style="text-align:right"><div><b>Invoice No:</b> ${esc(r.invoice_number)}</div><div><b>Date:</b> ${esc(fmtDMY(r.date))}</div></div></div>
<div class="c">${title}</div>
<table><tr><td class="no" style="border:1px solid #999"><b>Billed to</b><br>${esc(r.party_name)}<br>${r.gst_number ? 'GSTIN: ' + esc(r.gst_number) : ''}</td>
<td style="border:1px solid #999"><b>Supply</b><br>${r.supply_type === 'Inter' ? 'Inter-state (IGST)' : 'Intra-state (CGST + SGST)'}</td></tr></table><br>
<table><tr><th style="text-align:left">Description</th><th>HSN/SAC</th><th style="text-align:right">Taxable Value (₹)</th></tr>
<tr><td>${esc(r.description || r.head || 'Services')}</td><td style="text-align:center">${esc(r.hsn_sac || '')}</td><td style="text-align:right">${INR(r.taxable_value)}</td></tr></table><br>
<table style="width:55%;margin-left:auto">${row('Taxable value', INR(r.taxable_value))}
${sp.igst ? row('IGST @ ' + rate + '%', INR(sp.igst)) : row('CGST @ ' + (rate / 2) + '%', INR(sp.cgst)) + row('SGST @ ' + (rate / 2) + '%', INR(sp.sgst))}
<tr><td><b>${isCN ? 'Credit note value' : 'Invoice total'}</b></td><td style="text-align:right"><b>₹ ${INR(r.invoice_value)}</b></td></tr></table>
<p><b>Amount in words:</b> ${esc(amountInWords(Number(r.invoice_value || 0)))}</p>
<br><br><div style="text-align:right">For ${esc(COMPANY.name)}<br><br><br>Authorised Signatory</div>
<script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) { toast.error('Allow pop-ups to print the invoice'); return; }
  w.document.write(html); w.document.close();
}

// add one payment row (+ Accounts entry + refresh bill totals); returns an error or null
async function addPayment(K, bill, p, me) {
  const { data, error } = await supabase.from('gst_bill_payments').insert({ bill_type: K.key, bill_id: String(bill.id), ...p, created_by: me }).select().single();
  if (error) return error;
  const ref = `${K.source}_${bill.id}_p${data.id}`;
  await supabase.from('gst_bill_payments').update({ acct_ref: ref }).eq('id', data.id);
  const e2 = await syncPaymentToAccounts(K, bill, { ...data, acct_ref: ref });
  await refreshBillPaid(K, bill.id);
  return e2 || null;
}

// ── Main Component ─────────────────────────────────────────
export default function GST() {
  const { canView } = useAuth();
  const [tab, setTab] = useState(canView('gst_sales') ? 'sales' : canView('gst_purchase') ? 'purchase' : 'summary');
  const [pending, setPending] = useState(null);

  // Reminder: previous month's bills not yet marked "GST Filed"
  useEffect(() => {
    (async () => {
      try {
        const d = new Date(); d.setDate(1);
        const to = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
        d.setMonth(d.getMonth() - 1);
        const from = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
        const cnt = async t => {
          const { count } = await supabase.from(t).select('id', { count: 'exact', head: true })
            .gte('date', from).lt('date', to).or('remarks.is.null,remarks.neq."GST Filed"');
          return count || 0;
        };
        const [s, p] = await Promise.all([cnt('gst_sale_bills'), cnt('gst_purchase_bills')]);
        setPending(s + p > 0 ? { s, p, label: monthLabel(from.slice(0, 7)) } : null);
      } catch (e) { /* reminder is optional */ }
    })();
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">GST Purchase & Sales</h1>
          <p className="page-subtitle">GST sale bills, purchase bills, payments and monthly GST summary</p>
        </div>
      </div>
      {pending && (
        <div style={{background:'#fef3c7',border:'1.5px solid #f59e0b',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:13,color:'#92400e'}}>
          ⏰ <strong>{pending.label}</strong>: {pending.s} sale and {pending.p} purchase bill(s) are not marked <strong>GST Filed</strong>.
          <span style={{marginLeft:8,fontSize:12}}>(Monthly filers: GSTR-1 is due on the 11th and GSTR-3B on the 20th.)</span>
        </div>
      )}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {canView('gst_sales') && <div className={`tab ${tab==='sales'?'active':''}`} onClick={()=>setTab('sales')}>
          <FileText size={14} style={{marginRight:4}}/> GST Sale Bills
        </div>}
        {canView('gst_purchase') && <div className={`tab ${tab==='purchase'?'active':''}`} onClick={()=>setTab('purchase')}>
          <ShoppingCart size={14} style={{marginRight:4}}/> GST Purchase Bills
        </div>}
        {(canView('gst_sales') || canView('gst_purchase')) && <div className={`tab ${tab==='summary'?'active':''}`} onClick={()=>setTab('summary')}>
          <BarChart2 size={14} style={{marginRight:4}}/> GST Summary
        </div>}
      </div>
      {tab==='sales'    && <BillsTab key="sale" K={KINDS.sale} />}
      {tab==='purchase' && <BillsTab key="purchase" K={KINDS.purchase} />}
      {tab==='summary'  && <GSTSummary />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SALE / PURCHASE BILLS (one screen for both)
// ─────────────────────────────────────────────────────────────────
function BillsTab({ K }) {
  const { canEdit, user } = useAuth();
  const me = user?.email || '';
  const P = K.partyKey;
  const [records, setRecords] = useState([]);
  const [payMap, setPayMap]   = useState({});      // bill id -> payments[]
  const [showModal, setShowModal] = useState(false);
  const [showView,  setShowView]  = useState(null);
  const [payBill,   setPayBill]   = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [file, setFile]       = useState(null);
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef();

  // filters / sort / paging
  const [search, setSearch] = useState('');
  const [fCat, setFCat] = useState('');
  const [fDoc, setFDoc] = useState('');
  const [fRate, setFRate] = useState('');
  const [fFY, setFFY] = useState('');
  const [fMonth, setFMonth] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fRemark, setFRemark] = useState('');
  const [fPay, setFPay] = useState('');
  const [fItc, setFItc] = useState('');
  const [f2b, setF2b] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const emptyForm = {
    category:'B2B', doc_type:'Invoice', date:'', gst_number:'', party:'',
    invoice_number:'', hsn_sac:'', description:'',
    taxable_value:'', tax_rate:'0.18', supply_type:'Intra',
    head:'', remarks:'GST Filed',
    reverse_charge:false, itc_status:'Eligible', gstr2b_matched:false,
    attachment_path:'',
    pay_amount:'', pay_date:'', pay_type:'Cash', pay_mode:'', pay_cat:K.defCat, pay_receipt:'', pay_acct:'N',
  };
  const [form, setForm] = useState(emptyForm);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const taxAmt     = r2(Number(form.taxable_value||0) * Number(form.tax_rate||0));
  const invoiceVal = r2(Number(form.taxable_value||0) + taxAmt);
  const split      = splitTax(taxAmt, form.supply_type);

  useEffect(()=>{ load(); },[]);
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { load(); }, 800); };
    const ch = supabase.channel('gst-rt-' + K.key)
      .on('postgres_changes', {event:'*', schema:'public', table:K.table}, _reload)
      .on('postgres_changes', {event:'*', schema:'public', table:'gst_bill_payments'}, _reload)
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  async function load() {
    try {
      const [bills, pays] = await Promise.all([
        fetchAllPaged(() => supabase.from(K.table).select('*').order('date',{ascending:false}).order('id')),
        fetchAllPaged(() => supabase.from('gst_bill_payments').select('*').eq('bill_type', K.key).order('paid_date').order('id')).catch(()=>[]),
      ]);
      setRecords(bills);
      const m = {}; pays.forEach(p => { (m[p.bill_id] = m[p.bill_id] || []).push(p); });
      setPayMap(m);
    } catch (e) { toast.error('Could not load ' + K.title.toLowerCase() + ': ' + e.message); }
  }

  // ── filtering / sorting / paging ──
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const fr = fFY ? fyRange(fFY) : null;
    const list = records.filter(r => {
      const d = r.date || '';
      if (q && !((r[P]||'').toLowerCase().includes(q) || (r.invoice_number||'').toLowerCase().includes(q) || (r.gst_number||'').toLowerCase().includes(q) || (r.head||'').toLowerCase().includes(q) || (r.hsn_sac||'').toLowerCase().includes(q))) return false;
      if (fCat && r.category !== fCat) return false;
      if (fDoc && (r.doc_type||'Invoice') !== fDoc) return false;
      if (fRate && Number(r.tax_rate) !== Number(fRate)) return false;
      if (fr && (d < fr.from || d > fr.to)) return false;
      if (fMonth && !d.startsWith(fMonth)) return false;
      if (fFrom && d < fFrom) return false;
      if (fTo && d > fTo) return false;
      if (fRemark && r.remarks !== fRemark) return false;
      if (fPay) { const s = billStatus(r).label; if (fPay === 'open' ? !(s === 'Unpaid' || s === 'Part paid') : s !== fPay) return false; }
      if (K.itc && fItc && itcOf(r) !== fItc) return false;
      if (K.itc && f2b && (f2b === 'yes') !== !!r.gstr2b_matched) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = r => sortKey === 'party' ? (r[P]||'').toLowerCase() : sortKey === 'invoice' ? (r.invoice_number||'').toLowerCase()
      : sortKey === 'taxable' ? Number(r.taxable_value||0) : sortKey === 'total' ? Number(r.invoice_value||0)
      : sortKey === 'balance' ? billStatus(r).balance : (r.date||'');
    return [...list].sort((a,b) => { const x = val(a), y = val(b); return (x < y ? -1 : x > y ? 1 : 0) * dir; });
  }, [records, search, fCat, fDoc, fRate, fFY, fMonth, fFrom, fTo, fRemark, fPay, fItc, f2b, sortKey, sortDir]);

  useEffect(() => { setPage(1); }, [search, fCat, fDoc, fRate, fFY, fMonth, fFrom, fTo, fRemark, fPay, fItc, f2b, perPage]);
  const pages = perPage === 0 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = perPage === 0 ? filtered : filtered.slice((page-1)*perPage, page*perPage);

  const sum = f => filtered.reduce((s,r)=>s + signOf(r) * Number(f(r)||0), 0);
  const totTaxable = sum(r=>r.taxable_value), totTax = sum(r=>r.tax_amount), totInvoice = sum(r=>r.invoice_value);
  const totOutstanding = filtered.reduce((s,r)=>s + billStatus(r).balance, 0);

  const uniqueHeads = useMemo(() => [...new Set(records.map(r=>r.head).filter(Boolean))].sort(), [records]);
  const parties = useMemo(() => { const m = {}; records.forEach(r => { const n = (r[P]||'').trim(); if (n && !m[n.toLowerCase()]) m[n.toLowerCase()] = { name:n, gst:r.gst_number||'', cat:r.category||'B2B', head:r.head||'' }; else if (n && !m[n.toLowerCase()].gst && r.gst_number) m[n.toLowerCase()].gst = r.gst_number; }); return Object.values(m).sort((a,b)=>a.name.localeCompare(b.name)); }, [records]);
  const hasFilters = search||fCat||fDoc||fRate||fFY||fMonth||fFrom||fTo||fRemark||fPay||fItc||f2b;
  const clearFilters = () => { setSearch('');setFCat('');setFDoc('');setFRate('');setFFY('');setFMonth('');setFFrom('');setFTo('');setFRemark('');setFPay('');setFItc('');setF2b(''); };
  const toggleSort = k => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir(k === 'date' ? 'desc' : 'asc'); } };
  const sortMark = k => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const thSort = (k, label, style) => <th style={{cursor:'pointer',whiteSpace:'nowrap',...style}} onClick={()=>toggleSort(k)}>{label}{sortMark(k)}</th>;

  // suggested next invoice number (sale only)
  const nextInvoice = useMemo(() => {
    if (K.key !== 'sale') return '';
    const withNo = records.filter(r => /^(.*?)(\d+)$/.test((r.invoice_number||'').trim()) && (r.doc_type||'Invoice') === 'Invoice');
    if (!withNo.length) return '';
    const latest = [...withNo].sort((a,b) => (b.date||'').localeCompare(a.date||''))[0];
    const [, prefix] = latest.invoice_number.trim().match(/^(.*?)(\d+)$/);
    let max = 0, width = 0;
    withNo.forEach(r => { const m = r.invoice_number.trim().match(/^(.*?)(\d+)$/); if (m[1] === prefix && Number(m[2]) >= max) { max = Number(m[2]); width = m[2].length; } });
    return prefix + String(max + 1).padStart(width, '0');
  }, [records]);

  // ── bulk actions ──
  const allPageSelected = pageRows.length > 0 && pageRows.every(r => selected.has(r.id));
  const togglePage = () => setSelected(prev => { const n = new Set(prev); pageRows.forEach(r => allPageSelected ? n.delete(r.id) : n.add(r.id)); return n; });
  const toggleOne = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  async function bulkSetStatus(status, ids) {
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} bill(s) as "${status}"?`)) return;
    const { error } = await supabase.from(K.table).update({ remarks: status }).in('id', ids);
    if (error) toast.error(error.message); else { toast.success(`✅ ${ids.length} bills marked ${status}`); setSelected(new Set()); load(); }
  }
  async function bulkDelete() {
    const list = records.filter(r => selected.has(r.id));
    if (!list.length) return;
    if (!window.confirm(`Delete ${list.length} bill(s)? Their payments and synced Accounts entries are removed too. This cannot be undone.`)) return;
    try { await deleteBillsFully(K, list); toast.success(`🗑 ${list.length} bills deleted`); setSelected(new Set()); load(); }
    catch (e) { toast.error('Delete failed: ' + e.message); load(); }
  }

  // ── add / edit ──
  function openNew()  { setEditing(null); setFile(null); setForm(emptyForm); setShowModal(true); }
  function openEdit(r){
    setEditing(r.id); setFile(null);
    setForm({
      ...emptyForm,
      category:r.category||'B2B', doc_type:r.doc_type||'Invoice', date:r.date||'', gst_number:r.gst_number||'',
      party:r[P]||'', invoice_number:r.invoice_number||'', hsn_sac:r.hsn_sac||'', description:r.description||'',
      taxable_value:r.taxable_value!=null?String(r.taxable_value):'',
      tax_rate:r.tax_rate!=null?String(r.tax_rate):'0.18',
      supply_type:r.supply_type || supplyFromGstin(r.gst_number) || 'Intra',
      head:r.head||'', remarks:r.remarks||(K.key==='sale'?'GST Filed':''),
      reverse_charge:!!r.reverse_charge, itc_status:r.itc_status||'Eligible', gstr2b_matched:!!r.gstr2b_matched,
      attachment_path:r.attachment_path||'',
    });
    setShowModal(true);
  }
  function onPartyChange(v) {
    const m = parties.find(p => p.name.toLowerCase() === v.trim().toLowerCase());
    setForm(f => {
      const n = { ...f, party: v };
      if (m && !f.gst_number && m.gst) { n.gst_number = m.gst; n.category = m.cat || n.category; const s = supplyFromGstin(m.gst); if (s) n.supply_type = s; }
      if (m && !f.head && m.head) n.head = m.head;
      return n;
    });
  }
  function onGstChange(v) {
    const g = v.toUpperCase().replace(/\s/g, '');
    setForm(f => {
      const n = { ...f, gst_number: g };
      const s = supplyFromGstin(g); if (s) n.supply_type = s;
      if (g.length === 15 && !f.party.trim()) { const m = parties.find(p => p.gst === g); if (m) n.party = m.name; }
      return n;
    });
  }

  async function save() {
    const party = (form.party||'').trim(), inv = (form.invoice_number||'').trim(), gst = (form.gst_number||'').trim().toUpperCase();
    if (!inv || !form.date || !party) { toast.error(`Date, ${K.partyLabel} Name and Invoice Number are required`); return; }
    if (!(Number(form.taxable_value) > 0)) { toast.error('Enter the Taxable Value'); return; }
    if (form.category === 'B2B' && !gst && !window.confirm('This is a B2B bill without a GST number. Save anyway?')) return;
    if (gst && !gstinValid(gst) && !window.confirm(`GST number "${gst}" does not look valid (format/check digit). Save anyway?`)) return;
    const dup = records.find(r => r.id !== editing && (r.invoice_number||'').trim().toLowerCase() === inv.toLowerCase() && (r[P]||'').trim().toLowerCase() === party.toLowerCase() && (r.doc_type||'Invoice') === form.doc_type);
    if (dup && !window.confirm(`A ${form.doc_type.toLowerCase()} with invoice ${inv} for ${party} already exists. Save anyway?`)) return;
    if (!editing && form.pay_amount && !form.pay_date) { toast.error('Enter the payment date or clear the paid amount'); return; }
    if (file && file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`File is larger than ${MAX_FILE_MB} MB`); return; }
    setSaving(true);
    try {
      const orig = editing ? records.find(r=>r.id===editing) : null;
      let attachment_path = form.attachment_path || null;
      if (file) {
        const newPath = await uploadBillFile(K, file);
        if (orig?.attachment_path) await supabase.storage.from(BUCKET).remove([orig.attachment_path]);
        attachment_path = newPath;
      } else if (orig?.attachment_path && !form.attachment_path) {
        await supabase.storage.from(BUCKET).remove([orig.attachment_path]);
      }
      const payload = {
        category: form.category, doc_type: form.doc_type, date: form.date, gst_number: gst,
        [P]: party, invoice_number: inv, hsn_sac: form.hsn_sac.trim() || null, description: form.description.trim() || null,
        taxable_value: Number(form.taxable_value||0), tax_rate: Number(form.tax_rate||0),
        tax_amount: taxAmt, invoice_value: invoiceVal,
        supply_type: form.supply_type, cgst_amount: split.cgst, sgst_amount: split.sgst, igst_amount: split.igst,
        head: form.head, remarks: form.remarks, attachment_path,
      };
      if (K.itc) { payload.reverse_charge = !!form.reverse_charge; payload.itc_status = form.itc_status; payload.gstr2b_matched = !!form.gstr2b_matched; }
      let savedId = editing, error;
      if (editing) ({ error } = await supabase.from(K.table).update({ ...payload, updated_by: me, updated_at: new Date().toISOString() }).eq('id', editing));
      else { const r = await supabase.from(K.table).insert({ ...payload, created_by: me }).select('id').single(); error = r.error; savedId = r.data?.id; }
      if (error) throw error;

      if (editing && orig && (orig[P] !== party || orig.invoice_number !== inv)) {
        // bill name changed -> refresh the text of its Accounts entries
        const bill = { ...orig, ...payload, id: editing };
        for (const p of (payMap[String(editing)] || [])) { const e2 = await syncPaymentToAccounts(K, bill, p); if (e2) toast.error('Accounts sync: ' + e2.message); }
      }
      if (!editing && form.pay_amount && form.pay_date && savedId) {
        const e3 = await addPayment(K, { id: savedId, ...payload }, {
          paid_amount: Number(form.pay_amount), paid_date: form.pay_date, payment_type: form.pay_type, payment_mode: form.pay_mode,
          pay_category: form.pay_cat, receipt_no: form.pay_receipt, add_to_accounts: form.pay_acct,
        }, me);
        if (e3) toast.error('Bill saved but the payment failed: ' + e3.message);
      }
      toast.success(editing ? 'Updated ✅' : 'Saved ✅');
      setShowModal(false); load();
    } catch (e) { toast.error(msgFor(e)); }
    setSaving(false);
  }

  async function deleteOne(r) {
    if (!window.confirm('Delete this bill? Its payments and synced Accounts entries are removed too.')) return;
    try { await deleteBillsFully(K, [r]); load(); } catch (e) { toast.error(e.message); }
  }

  // ── export ──
  function exportExcel() {
    const rows = filtered.map((r,i)=>{ const s = signOf(r), sp = splitOf(r), st = billStatus(r); return {
      'Sl':i+1, 'Doc Type':r.doc_type||'Invoice', 'Category':r.category||'', 'Date':r.date?fmtDMY(r.date):'', 'GST Number':r.gst_number||'',
      [K.partyLabel+' Name']:r[P]||'', 'Invoice Number':r.invoice_number||'', 'HSN/SAC':r.hsn_sac||'',
      'Taxable Value':s*Number(r.taxable_value||0), 'Tax Rate':`${(Number(r.tax_rate||0)*100).toFixed(0)}%`,
      'CGST':s*sp.cgst, 'SGST':s*sp.sgst, 'IGST':s*sp.igst,
      'Tax Amount':s*Number(r.tax_amount||0), 'Invoice Value':s*Number(r.invoice_value||0),
      'Paid':Number(r.paid_amount||0), 'Balance':st.balance, 'Payment Status':st.label,
      ...(K.itc ? { 'ITC Status':itcOf(r), 'In GSTR-2B':r.gstr2b_matched?'Yes':'No', 'Reverse Charge':r.reverse_charge?'Yes':'No' } : {}),
      'Head':r.head||'', 'GST Status':r.remarks||'',
    }; });
    if (!rows.length) { toast.error('Nothing to export'); return; }
    const sp = filtered.reduce((a,r)=>{ const x = splitOf(r), s = signOf(r); a.c += s*x.cgst; a.s += s*x.sgst; a.i += s*x.igst; return a; }, {c:0,s:0,i:0});
    rows.push({ 'Sl':'TOTAL', 'Taxable Value':totTaxable, 'CGST':sp.c, 'SGST':sp.s, 'IGST':sp.i, 'Tax Amount':totTax, 'Invoice Value':totInvoice, 'Balance':totOutstanding });
    const widths = {}; Object.keys(rows[0]).forEach(k => { widths[k] = /Name|Head/.test(k) ? 30 : /Number|Status|Type/.test(k) ? 16 : 13; }); widths['Sl'] = 6;
    saveStyledXlsx(`${K.title.replace(/ /g,'_')}_${new Date().getFullYear()}.xlsx`, [{ name:K.title, title:K.title + (fFY ? ' — FY ' + fyLabel(fFY) : '') + (fMonth ? ' — ' + fMonth : ''), rows, widths }]);
    toast.success('Exported ✅');
  }

  // ── import ──
  function downloadTemplate() {
    const head = ['Category','Doc Type','Date','GST Number',K.partyLabel+' Name','Invoice Number','HSN/SAC','Taxable Value','Tax Rate','Head','Remarks'];
    const ws = XLSX.utils.aoa_to_sheet([head, ['B2B','Invoice','15-04-2026','32AAPFU0939F1ZV','Sample Party','INV/2026/0001','9992','10000','18%','Training','GST Filed']]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Template'); XLSX.writeFile(wb, `${K.title.replace(/ /g,'_')}_import_template.xlsx`);
  }
  async function handleImport(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const wb  = XLSX.read(buf, {type:'array', cellDates:true});
      const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {header:1, defval:''});
      if (raw.length < 2) { toast.error('File empty'); return; }
      const headers = raw[0].map(h=>String(h||'').toLowerCase().trim());
      const rows = raw.slice(1).filter(r=>r.some(v=>v!==''&&v!==null));
      // exact heading first, then "contains" — so "Invoice Date" is never mistaken for "Invoice Number"
      const get = (row, names) => {
        for (const n of names) { const i = headers.indexOf(n); if (i >= 0) return row[i]; }
        for (const n of names) { const i = headers.findIndex(x=>x.includes(n)); if (i >= 0) return row[i]; }
        return '';
      };
      const txt = v => (v instanceof Date ? '' : String(v==null?'':v)).trim();
      const partyNames = K.key === 'sale' ? ['party name','party','customer'] : ['supplier name','supplier','party name','party'];
      const toInsert = rows.map(row=>{
        const taxable = Number(txt(get(row,['taxable value','taxable amount','taxable'])).replace(/[^0-9.]/g,'')||0);
        const rate    = Number(txt(get(row,['tax rate','gst rate','rate'])).replace(/[^0-9.]/g,'')||0);
        const rateVal = rate > 1 ? rate/100 : rate;
        const gst = txt(get(row,['gst number','gstin','gst no','gst'])).toUpperCase().replace(/\s/g,'');
        const dt = txt(get(row,['doc type','document type']));
        const doc = /credit/i.test(dt) ? 'Credit Note' : /debit/i.test(dt) ? 'Debit Note' : 'Invoice';
        const tax = r2(taxable * rateVal);
        const supply = /inter/i.test(txt(get(row,['supply type','place of supply']))) ? 'Inter' : /intra/i.test(txt(get(row,['supply type','place of supply']))) ? 'Intra' : (supplyFromGstin(gst) || 'Intra');
        const sp = splitTax(tax, supply);
        return {
          category: txt(get(row,['category','categ'])) || (gst ? 'B2B' : 'B2C'), doc_type: doc,
          date: parseXlDate(get(row,['date','invoice date','bill date'])),
          gst_number: gst, [P]: txt(get(row,partyNames)),
          invoice_number: txt(get(row,['invoice number','invoice no','bill no','invoice'])),
          hsn_sac: txt(get(row,['hsn/sac','hsn','sac'])) || null,
          taxable_value: taxable, tax_rate: rateVal, tax_amount: tax, invoice_value: r2(taxable + tax),
          supply_type: supply, cgst_amount: sp.cgst, sgst_amount: sp.sgst, igst_amount: sp.igst,
          head: txt(get(row,['head'])), remarks: txt(get(row,['remarks','gst status','status'])) || 'GST Filed',
          created_by: me,
        };
      }).filter(r=>r[P]||r.invoice_number);
      if (!toInsert.length) { toast.error('No valid rows found'); return; }
      const keyOf = r => `${(r[P]||'').toLowerCase()}|${(r.invoice_number||'').toLowerCase()}|${r.doc_type||'Invoice'}`;
      const have = new Set(records.map(keyOf));
      const fresh = toInsert.filter(r => { const k = keyOf(r); if (have.has(k)) return false; have.add(k); return true; });
      const skipped = toInsert.length - fresh.length;
      const noDate = fresh.filter(r => !r.date).length;
      const badGst = fresh.filter(r => r.gst_number && !gstinValid(r.gst_number)).length;
      if (!fresh.length) { toast.error(`All ${toInsert.length} rows already exist — nothing imported`); return; }
      let msg = '';
      if (noDate) msg += `${noDate} row(s) have no readable date.\n`;
      if (badGst) msg += `${badGst} row(s) have a GST number that looks invalid.\n`;
      if (msg && !window.confirm(msg + 'Import anyway?')) return;
      for (let i = 0; i < fresh.length; i += 200) {
        const { error } = await supabase.from(K.table).insert(fresh.slice(i, i+200));
        if (error) { toast.error('Import failed: ' + msgFor(error)); load(); return; }
      }
      toast.success(`✅ ${fresh.length} bills imported${skipped?` (${skipped} duplicate skipped)`:''}`); load(); setShowImport(false);
    } finally { if (fileRef.current) fileRef.current.value=''; }
  }

  // ── payments modal state ──
  const emptyPay = { paid_amount:'', paid_date:'', payment_type:'Cash', payment_mode:'', pay_category:K.defCat, receipt_no:'', add_to_accounts:'N' };
  const [payForm, setPayForm] = useState(emptyPay);
  const [payEditing, setPayEditing] = useState(null);
  const spf = (k,v) => setPayForm(f=>({...f,[k]:v}));
  const livePB = payBill ? (records.find(x=>x.id===payBill.id) || payBill) : null;
  useEffect(() => {
    if (!payBill) return;
    const st = billStatus(records.find(x=>x.id===payBill.id) || payBill);
    setPayEditing(null);
    setPayForm({ ...emptyPay, paid_amount: st.balance > 0 ? String(st.balance) : '', paid_date: todayStr() });
  }, [payBill?.id]);

  async function savePayment() {
    const bill = livePB;
    const amt = Number(payForm.paid_amount);
    if (!(amt > 0) || !payForm.paid_date) { toast.error('Enter the amount and payment date'); return; }
    const others = (payMap[String(bill.id)] || []).filter(p => p.id !== payEditing).reduce((s,p)=>s+Number(p.paid_amount||0),0);
    if (others + amt > Number(bill.invoice_value||0) + 0.01 && !window.confirm(`Total paid (₹${INR(others+amt)}) will exceed the invoice value (₹${INR(bill.invoice_value)}). Continue?`)) return;
    try {
      const row = { paid_amount: amt, paid_date: payForm.paid_date, payment_type: payForm.payment_type, payment_mode: payForm.payment_mode, pay_category: payForm.pay_category, receipt_no: payForm.receipt_no, add_to_accounts: payForm.add_to_accounts };
      if (payEditing) {
        const old = (payMap[String(bill.id)] || []).find(p => p.id === payEditing);
        const { error } = await supabase.from('gst_bill_payments').update(row).eq('id', payEditing);
        if (error) throw error;
        const ref = old.acct_ref || `${K.source}_${bill.id}_p${payEditing}`;
        if (!old.acct_ref) await supabase.from('gst_bill_payments').update({ acct_ref: ref }).eq('id', payEditing);
        const e2 = await syncPaymentToAccounts(K, bill, { ...old, ...row, acct_ref: ref });
        await refreshBillPaid(K, bill.id);
        if (e2) toast.error('Saved, but Accounts sync failed: ' + e2.message);
      } else {
        const e1 = await addPayment(K, bill, row, me);
        if (e1) toast.error('Accounts sync failed: ' + e1.message);
      }
      toast.success(payEditing ? 'Payment updated ✅' : 'Payment added ✅');
      setPayEditing(null); setPayForm({ ...emptyPay, paid_date: todayStr() });
      load();
    } catch (e) { toast.error(msgFor(e)); }
  }
  async function removePayment(p) {
    if (!window.confirm('Delete this payment? Its Accounts entry is removed too.')) return;
    try {
      await removePaymentFromAccounts(K, livePB, p);
      const { error } = await supabase.from('gst_bill_payments').delete().eq('id', p.id);
      if (error) throw error;
      await refreshBillPaid(K, livePB.id);
      load();
    } catch (e) { toast.error(msgFor(e)); }
  }

  // ── Add / Edit modal ──
  function renderForm() {
    const gOk = form.gst_number && form.gst_number.length === 15 ? gstinValid(form.gst_number) : null;
    return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
      <div className="modal modal-lg" style={{maxWidth:760,maxHeight:'94vh',overflowY:'auto'}}>
        <div className="modal-header">
          <span className="modal-title">{editing?'Edit':'Add'} {K.key==='sale'?'GST Sale Bill':'GST Purchase Bill'}</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label>Document Type</label>
              <select value={form.doc_type} onChange={e=>sf('doc_type',e.target.value)}>
                {DOC_TYPES.map(c=><option key={c} value={c}>{c}{c==='Credit Note'?' (reduces tax)':c==='Debit Note'?' (adds tax)':''}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Category *</label>
              <select value={form.category} onChange={e=>sf('category',e.target.value)}>
                {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Date *</label><DateInput value={form.date} onChange={e=>sf('date',e.target.value)}/></div>
            <div className="form-group"><label>GST Number {gOk===true&&<span style={{color:'var(--green)',fontSize:11}}>✓ valid</span>}{gOk===false&&<span style={{color:'var(--red)',fontSize:11}}>✗ check number</span>}</label>
              <input value={form.gst_number} onChange={e=>onGstChange(e.target.value)} maxLength={15} placeholder="e.g. 32AABAS3630A1ZA" style={{fontFamily:'monospace',borderColor:gOk===false?'var(--red)':undefined}}/>
            </div>
            <div className="form-group full"><label>{K.partyLabel} Name *</label>
              <input value={form.party} onChange={e=>onPartyChange(e.target.value)} placeholder={K.partyWord + ' name'} list="party-suggestions"/>
              <datalist id="party-suggestions">{parties.map(p=><option key={p.name} value={p.name}/>)}</datalist>
            </div>
            <div className="form-group"><label>Invoice Number *</label>
              <input value={form.invoice_number} onChange={e=>sf('invoice_number',e.target.value)} placeholder="e.g. AEPL/2026/0001" style={{fontFamily:'monospace'}}/>
              {K.key==='sale'&&!editing&&nextInvoice&&form.invoice_number!==nextInvoice&&(
                <div style={{fontSize:11,marginTop:3,color:'var(--text3)'}}>Next number: <button type="button" className="btn btn-ghost btn-sm" style={{padding:'0 6px',fontFamily:'monospace'}} onClick={()=>sf('invoice_number',nextInvoice)}>{nextInvoice}</button></div>
              )}
            </div>
            <div className="form-group"><label>Head / Site</label>
              <input value={form.head} onChange={e=>sf('head',e.target.value)} placeholder="e.g. Kummil site, Training" list="head-suggestions"/>
              <datalist id="head-suggestions">{uniqueHeads.map(h=><option key={h} value={h}/>)}</datalist>
            </div>
            <div className="form-group"><label>HSN / SAC</label><input value={form.hsn_sac} onChange={e=>sf('hsn_sac',e.target.value)} placeholder="e.g. 999293"/></div>
            <div className="form-group"><label>Description</label><input value={form.description} onChange={e=>sf('description',e.target.value)} placeholder="Goods / service description"/></div>
          </div>

          <div style={{background:'var(--accent-light)',border:'1.5px solid var(--accent)',borderRadius:10,padding:'14px 16px',marginTop:14}}>
            <div style={{fontWeight:700,fontSize:12,color:'var(--accent)',marginBottom:12}}>Tax Calculation {form.doc_type==='Credit Note'&&<span style={{color:'var(--red)'}}> — Credit Note: amounts are deducted in totals</span>}</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
              <div className="form-group" style={{margin:0}}><label>Taxable Value (₹)</label>
                <input type="number" step="0.01" value={form.taxable_value} onChange={e=>sf('taxable_value',e.target.value)} placeholder="0.00"/></div>
              <div className="form-group" style={{margin:0}}><label>Tax Rate</label>
                <select value={form.tax_rate} onChange={e=>sf('tax_rate',e.target.value)}>{TAX_RATES.map(r=><option key={r} value={r}>{(r*100).toFixed(0)}% GST</option>)}</select></div>
              <div className="form-group" style={{margin:0}}><label>Tax Amount <span style={{fontSize:10,color:'var(--accent)'}}>auto</span></label>
                <input value={taxAmt.toFixed(2)} readOnly style={{background:'var(--bg3)',color:'var(--amber)',fontWeight:700,cursor:'not-allowed'}}/></div>
              <div className="form-group" style={{margin:0}}><label>Invoice Value <span style={{fontSize:10,color:'var(--green)'}}>auto</span></label>
                <input value={invoiceVal.toFixed(2)} readOnly style={{background:'var(--bg3)',color:'var(--green)',fontWeight:800,cursor:'not-allowed'}}/></div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',gap:10,marginTop:10,alignItems:'end'}}>
              <div className="form-group" style={{margin:0}}><label>Place of supply</label>
                <select value={form.supply_type} onChange={e=>sf('supply_type',e.target.value)}>{SUPPLY_TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>
              <div style={{fontSize:12}}>CGST<br/><strong>₹{INR(split.cgst)}</strong></div>
              <div style={{fontSize:12}}>SGST<br/><strong>₹{INR(split.sgst)}</strong></div>
              <div style={{fontSize:12}}>IGST<br/><strong>₹{INR(split.igst)}</strong></div>
            </div>
            <div style={{marginTop:10,padding:'8px 12px',background:'rgba(255,255,255,0.5)',borderRadius:6,fontSize:12,textAlign:'center',color:'var(--text2)'}}>
              Invoice Value = ₹{INR(Number(form.taxable_value||0))} + ₹{INR(taxAmt)} ({(Number(form.tax_rate)*100).toFixed(0)}% tax) = <strong style={{color:'var(--green)',fontSize:14}}>₹{INR(invoiceVal)}</strong>
            </div>
          </div>

          <div className="form-grid" style={{marginTop:14}}>
            <div className="form-group"><label>GST Status</label>
              <select value={form.remarks} onChange={e=>sf('remarks',e.target.value)}>
                {!form.remarks&&<option value="">— not set —</option>}
                {GST_REMARKS.map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {K.itc&&<div className="form-group"><label>ITC (Input Tax Credit)</label>
              <select value={form.itc_status} onChange={e=>sf('itc_status',e.target.value)}>{ITC_STATUS.map(r=><option key={r} value={r}>{r}</option>)}</select>
            </div>}
            {K.itc&&<div className="form-group"><label>&nbsp;</label>
              <label style={{display:'flex',gap:6,alignItems:'center',fontWeight:500}}><input type="checkbox" checked={form.gstr2b_matched} onChange={e=>sf('gstr2b_matched',e.target.checked)}/> Appears in GSTR-2B</label>
              <label style={{display:'flex',gap:6,alignItems:'center',fontWeight:500,marginTop:4}}><input type="checkbox" checked={form.reverse_charge} onChange={e=>sf('reverse_charge',e.target.checked)}/> Reverse charge (RCM)</label>
            </div>}
          </div>

          <div className="form-section-heading" style={{marginTop:14}}>📎 Bill copy (PDF / image, max {MAX_FILE_MB} MB)</div>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <input type="file" accept=".pdf,image/*" onChange={e=>setFile(e.target.files?.[0]||null)}/>
            {form.attachment_path&&!file&&<>
              <button type="button" className="btn btn-ghost btn-sm" onClick={()=>openBillFile(form.attachment_path)}><Paperclip size={12}/> Current file</button>
              <button type="button" className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>sf('attachment_path','')}>Remove</button>
            </>}
          </div>

          {!editing&&(<>
            <div className="form-section-heading" style={{marginTop:14}}>💳 Payment (optional — more payments can be added later with the 💳 button)</div>
            <div className="form-grid">
              <div className="form-group"><label>Paid Amount (₹)</label><input type="number" value={form.pay_amount} onChange={e=>sf('pay_amount',e.target.value)} placeholder="0.00"/></div>
              <div className="form-group"><label>Paid Date</label><DateInput value={form.pay_date} onChange={e=>sf('pay_date',e.target.value)}/></div>
              <div className="form-group"><label>Payment Type</label><select value={form.pay_type} onChange={e=>sf('pay_type',e.target.value)}>{PAYMENT_TYPES_LIST.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
              <div className="form-group"><label>Payment Mode</label><select value={form.pay_mode} onChange={e=>sf('pay_mode',e.target.value)}><option value="">Select Account</option>{PAYMENT_MODES_LIST.map(m=><option key={m} value={m}>{m}</option>)}</select></div>
              <div className="form-group"><label>Category</label><select value={form.pay_cat} onChange={e=>sf('pay_cat',e.target.value)}>{PAYMENT_CATS_LIST.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
              <div className="form-group"><label>Receipt / Reference No</label><input value={form.pay_receipt} onChange={e=>sf('pay_receipt',e.target.value)} placeholder="Receipt or reference number"/></div>
              <div className="form-group"><label>Add to Accounts?</label>
                <select value={form.pay_acct} onChange={e=>sf('pay_acct',e.target.value)} style={{borderColor:form.pay_acct==='Y'?'var(--green)':'var(--border)',fontWeight:600}}>
                  <option value="N">N — No</option><option value="Y">Y — Yes (auto-sync to Accounts)</option>
                </select>
              </div>
            </div>
          </>)}
          {editing&&<div style={{marginTop:12,fontSize:12,color:'var(--text3)'}}>Payments for this bill are managed with the 💳 button in the table.</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Save'}</button>
        </div>
      </div>
    </div>
  ); }

  // ── View modal ──
  function renderView() {
    const v = records.find(x=>x.id===showView.id) || showView;
    const st = billStatus(v), sp = splitOf(v), pays = payMap[String(v.id)] || [];
    const rows = [
      ['Invoice No', v.invoice_number], ['Document', v.doc_type||'Invoice'], ['Date', fmtDMY(v.date)],
      [K.partyLabel+' Name', v[P]], ['GST No', v.gst_number||'—'], ['Category', v.category],
      ['HSN / SAC', v.hsn_sac||'—'], ['Description', v.description||'—'],
      ['Taxable Value', '₹'+INR(v.taxable_value)], ['Tax Rate', (Number(v.tax_rate||0)*100).toFixed(0)+'%'],
      ['Supply', (v.supply_type || supplyFromGstin(v.gst_number) || 'Intra') === 'Inter' ? 'Inter-state (IGST)' : 'Intra-state (CGST + SGST)'],
      ['CGST / SGST / IGST', `₹${INR(sp.cgst)} / ₹${INR(sp.sgst)} / ₹${INR(sp.igst)}`],
      ['Tax Amount', '₹'+INR(v.tax_amount)], ['Invoice Value', '₹'+INR(v.invoice_value)],
      ['Head', v.head||'—'], ['GST Status', v.remarks||'—'],
      ...(K.itc ? [['ITC', itcOf(v) + (v.gstr2b_matched ? ' · in GSTR-2B' : ' · not in GSTR-2B') + (v.reverse_charge ? ' · reverse charge' : '')]] : []),
      ['Payment', `${st.label} — paid ₹${INR(v.paid_amount||0)}${st.balance>0?`, balance ₹${INR(st.balance)}`:''}`],
      ['Added by', v.created_by ? `${v.created_by}${v.created_at ? ' · ' + fmtDMY(String(v.created_at).slice(0,10)) : ''}` : '—'],
      ['Last edited by', v.updated_by ? `${v.updated_by}${v.updated_at ? ' · ' + fmtDMY(String(v.updated_at).slice(0,10)) : ''}` : '—'],
    ];
    return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowView(null)}>
      <div className="modal" style={{maxWidth:600,maxHeight:'94vh',overflowY:'auto'}}>
        <div className="modal-header"><span className="modal-title">📋 {K.title.replace(' Bills',' Bill')} Details</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowView(null)}><X size={16}/></button></div>
        <div className="modal-body">
          {rows.map(([l,val])=>(
            <div key={l} style={{display:'flex',gap:12,padding:'7px 0',borderBottom:'1px solid var(--border)'}}>
              <div style={{width:140,fontWeight:600,fontSize:12,color:'var(--text2)',flexShrink:0}}>{l}</div>
              <div style={{fontSize:13,color:'var(--text)'}}>{val}</div>
            </div>
          ))}
          {v.attachment_path&&<div style={{marginTop:10}}><button className="btn btn-secondary btn-sm" onClick={()=>openBillFile(v.attachment_path)}><Paperclip size={13}/> Open bill copy</button></div>}
          {pays.length>0&&(<div style={{marginTop:12}}>
            <div style={{fontWeight:700,fontSize:12,marginBottom:4}}>Payments</div>
            {pays.map(p=><div key={p.id} style={{fontSize:12,padding:'3px 0'}}>{fmtDMY(p.paid_date)} — ₹{INR(p.paid_amount)} · {p.payment_type}{p.payment_mode?' · '+p.payment_mode:''}{p.receipt_no?' · '+p.receipt_no:''}</div>)}
          </div>)}
        </div>
        <div className="modal-footer">
          {K.key==='sale'&&<button className="btn btn-secondary" onClick={()=>printInvoice(v)}><Printer size={13}/> Print invoice</button>}
          {canEdit('gst')&&<button className="btn btn-secondary" onClick={()=>{setShowView(null);setPayBill(v);}}><CreditCard size={13}/> Payments</button>}
          {canEdit('gst')&&<button className="btn btn-secondary" onClick={()=>{setShowView(null);openEdit(v);}}>Edit</button>}
          <button className="btn btn-primary" onClick={()=>setShowView(null)}>Close</button>
        </div>
      </div>
    </div>
  ); }

  // ── Payments modal ──
  function renderPayments() {
    const b = livePB, st = billStatus(b), pays = payMap[String(b.id)] || [];
    return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setPayBill(null)}>
      <div className="modal" style={{maxWidth:640,maxHeight:'94vh',overflowY:'auto'}}>
        <div className="modal-header"><span className="modal-title">💳 Payments — {b.invoice_number} · {b[P]}</span><button className="btn btn-ghost btn-sm" onClick={()=>setPayBill(null)}><X size={16}/></button></div>
        <div className="modal-body">
          <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:12,fontSize:13}}>
            <div>Invoice value<br/><strong>₹{INR(b.invoice_value)}</strong></div>
            <div>Paid<br/><strong style={{color:'var(--green)'}}>₹{INR(b.paid_amount||0)}</strong></div>
            <div>Balance<br/><strong style={{color:st.balance>0?'var(--red)':'var(--green)'}}>₹{INR(st.balance)}</strong></div>
            <div><span className={`badge ${st.cls}`}>{st.label}</span></div>
          </div>
          {pays.length===0 ? <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>No payments recorded yet.</div> : (
            <div className="table-wrap" style={{marginBottom:12}}>
              <table><thead><tr><th>Date</th><th style={{textAlign:'right'}}>Amount</th><th>Type / Mode</th><th>Receipt</th><th>Accounts</th><th></th></tr></thead>
              <tbody>{pays.map(p=>(
                <tr key={p.id}>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDMY(p.paid_date)}</td>
                  <td style={{textAlign:'right',fontWeight:600}}>₹{INR(p.paid_amount)}</td>
                  <td style={{fontSize:11}}>{p.payment_type}{p.payment_mode?' · '+p.payment_mode:''}</td>
                  <td style={{fontSize:11}}>{p.receipt_no||'—'}</td>
                  <td>{p.add_to_accounts==='Y'?<span className="badge badge-green" style={{fontSize:9}}>Synced</span>:'—'}</td>
                  <td style={{whiteSpace:'nowrap'}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setPayEditing(p.id);setPayForm({paid_amount:String(p.paid_amount),paid_date:p.paid_date||'',payment_type:p.payment_type||'Cash',payment_mode:p.payment_mode||'',pay_category:p.pay_category||K.defCat,receipt_no:p.receipt_no||'',add_to_accounts:p.add_to_accounts||'N'});}}><Edit2 size={12}/></button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>removePayment(p)}><Trash2 size={12}/></button>
                  </td>
                </tr>))}</tbody></table>
            </div>
          )}
          <div className="form-section-heading">{payEditing?'Edit payment':'Add payment'}</div>
          <div className="form-grid">
            <div className="form-group"><label>Amount (₹)</label><input type="number" value={payForm.paid_amount} onChange={e=>spf('paid_amount',e.target.value)}/></div>
            <div className="form-group"><label>Date</label><DateInput value={payForm.paid_date} onChange={e=>spf('paid_date',e.target.value)}/></div>
            <div className="form-group"><label>Payment Type</label><select value={payForm.payment_type} onChange={e=>spf('payment_type',e.target.value)}>{PAYMENT_TYPES_LIST.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
            <div className="form-group"><label>Payment Mode</label><select value={payForm.payment_mode} onChange={e=>spf('payment_mode',e.target.value)}><option value="">Select Account</option>{PAYMENT_MODES_LIST.map(m=><option key={m} value={m}>{m}</option>)}</select></div>
            <div className="form-group"><label>Category</label><select value={payForm.pay_category} onChange={e=>spf('pay_category',e.target.value)}>{PAYMENT_CATS_LIST.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
            <div className="form-group"><label>Receipt / Reference No</label><input value={payForm.receipt_no} onChange={e=>spf('receipt_no',e.target.value)}/></div>
            <div className="form-group"><label>Add to Accounts?</label>
              <select value={payForm.add_to_accounts} onChange={e=>spf('add_to_accounts',e.target.value)} style={{borderColor:payForm.add_to_accounts==='Y'?'var(--green)':'var(--border)',fontWeight:600}}>
                <option value="N">N — No</option><option value="Y">Y — Yes (auto-sync to Accounts → {K.cashType})</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          {payEditing&&<button className="btn btn-ghost" onClick={()=>{setPayEditing(null);setPayForm({...emptyPay,paid_date:todayStr()});}}>Cancel edit</button>}
          <button className="btn btn-secondary" onClick={()=>setPayBill(null)}>Close</button>
          <button className="btn btn-primary" onClick={savePayment}>{payEditing?'Update payment':'Add payment'}</button>
        </div>
      </div>
    </div>
  ); }

  // ── Import modal ──
  function renderImport() { return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowImport(false)}>
      <div className="modal" style={{maxWidth:560}}>
        <div className="modal-header"><span className="modal-title">📥 Import {K.title} from Excel</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowImport(false)}><X size={16}/></button></div>
        <div className="modal-body">
          <div style={{background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:10,padding:14,marginBottom:16}}>
            <div style={{fontWeight:700,marginBottom:8}}>Columns (any order; names are matched exactly first):</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,fontSize:12}}>
              {['Category','Doc Type (optional)','Date','GST Number',K.partyLabel+' Name','Invoice Number','HSN/SAC (optional)','Taxable Value','Tax Rate','Head','Remarks'].map(col=>(
                <div key={col} style={{background:'rgba(255,255,255,0.5)',borderRadius:4,padding:'3px 8px'}}>{col}</div>
              ))}
            </div>
            <div style={{fontSize:11,color:'var(--text3)',marginTop:8}}>
              Tax Amount, CGST/SGST/IGST and Invoice Value are calculated on import. Tax Rate can be 0.18 or 18%. The supply type comes from the GST number (32… = intra-state).
            </div>
            <button className="btn btn-ghost btn-sm" style={{marginTop:8}} onClick={downloadTemplate}><Download size={13}/> Download Excel template</button>
          </div>
          <div style={{border:'2px dashed var(--border)',borderRadius:10,padding:30,textAlign:'center',cursor:'pointer'}} onClick={()=>fileRef.current?.click()}>
            <Upload size={32} style={{color:'var(--accent)',marginBottom:8}}/>
            <div style={{fontWeight:600,marginBottom:4}}>Click to upload Excel or CSV</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleImport}/>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowImport(false)}>Close</button></div>
      </div>
    </div>
  ); }

  const ratePct = r => `${(Number(r.tax_rate||0)*100).toFixed(0)}%`;
  const colCount = 14 + (canEdit("gst") ? 1 : 0);

  return (
    <div>
      {/* Summary */}
      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          { label:'Total Bills',        value:filtered.length, color:'var(--accent)', fmt:false },
          { label:'Total Taxable',      value:totTaxable,      color:'var(--text2)',  fmt:true  },
          { label:'Total Tax Amount',   value:totTax,          color:'var(--amber)',  fmt:true  },
          { label:'Total Invoice Value',value:totInvoice,      color:'var(--green)',  fmt:true  },
          { label: K.key==='sale' ? 'Receivable (unpaid)' : 'Payable (unpaid)', value:totOutstanding, color:'var(--red)', fmt:true },
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{color:s.color,fontSize:s.fmt?15:22}}>{s.fmt ? `₹${INR(s.value)}` : s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:220}}>
            <Search/><input placeholder={`Search ${K.partyLabel.toLowerCase()}, invoice, GST no, head, HSN...`} value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select style={{width:80,fontSize:12,padding:'4px 6px'}} value={fCat} onChange={e=>setFCat(e.target.value)}>
            <option value="">All Cat.</option>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{width:105,fontSize:12,padding:'4px 6px'}} value={fDoc} onChange={e=>setFDoc(e.target.value)}>
            <option value="">All Docs</option>{DOC_TYPES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{width:90,fontSize:12,padding:'4px 6px'}} value={fRate} onChange={e=>setFRate(e.target.value)}>
            <option value="">All Rates</option>{TAX_RATES.map(r=><option key={r} value={r}>{(r*100).toFixed(0)}%</option>)}
          </select>
          <select style={{width:105,fontSize:12,padding:'4px 6px'}} value={fFY} onChange={e=>setFFY(e.target.value)}>
            <option value="">All FY</option>{fyOptions().map(y=><option key={y} value={y}>FY {fyLabel(y)}</option>)}
          </select>
          <select style={{width:120,fontSize:12,padding:'4px 6px'}} value={fRemark} onChange={e=>setFRemark(e.target.value)}>
            <option value="">All Status</option>{GST_REMARKS.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
          <select style={{width:120,fontSize:12,padding:'4px 6px'}} value={fPay} onChange={e=>setFPay(e.target.value)}>
            <option value="">All Payments</option><option value="open">Not fully paid</option><option value="Unpaid">Unpaid</option><option value="Part paid">Part paid</option><option value="Paid">Paid</option>
          </select>
          {K.itc&&<select style={{width:110,fontSize:12,padding:'4px 6px'}} value={fItc} onChange={e=>setFItc(e.target.value)}>
            <option value="">All ITC</option>{ITC_STATUS.map(c=><option key={c} value={c}>ITC {c}</option>)}
          </select>}
          {K.itc&&<select style={{width:110,fontSize:12,padding:'4px 6px'}} value={f2b} onChange={e=>setF2b(e.target.value)}>
            <option value="">GSTR-2B: any</option><option value="yes">In 2B</option><option value="no">Not in 2B</option>
          </select>}
          <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'3px 10px'}}>
            <span style={{fontSize:11,color:'var(--text3)',whiteSpace:'nowrap'}}>📅 Month:</span>
            <input type="month" value={fMonth} onChange={e=>setFMonth(e.target.value)} style={{fontSize:12,border:'none',background:'transparent',outline:'none',padding:'2px 0',minWidth:130}}/>
            {fMonth&&<button onClick={()=>setFMonth('')} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:14,lineHeight:1,padding:'0 2px'}}>✕</button>}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:4}}>
            <span style={{fontSize:11,color:'var(--text3)'}}>From</span><DateInput value={fFrom} onChange={e=>setFFrom(e.target.value)} style={{fontSize:12,width:120}}/>
            <span style={{fontSize:11,color:'var(--text3)'}}>To</span><DateInput value={fTo} onChange={e=>setFTo(e.target.value)} style={{fontSize:12,width:120}}/>
          </div>
          {hasFilters&&<button className="btn btn-ghost btn-sm" onClick={clearFilters}>✕ Clear All</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} bills</span>
        </div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {canEdit('gst')&&fMonth&&filtered.length>0&&<>
            <button className="btn btn-secondary btn-sm" style={{background:'#f0fdf4',color:'var(--green)',border:'1px solid #86efac'}} onClick={()=>bulkSetStatus('GST Filed', filtered.map(r=>r.id))}>✅ Mark {fMonth} Filed</button>
            <button className="btn btn-secondary btn-sm" style={{background:'#fef2f2',color:'var(--red)',border:'1px solid #fca5a5'}} onClick={()=>bulkSetStatus('GST Not Filed', filtered.map(r=>r.id))}>❌ Mark Not Filed</button>
          </>}
          <button className="btn btn-secondary" onClick={exportExcel}><Download size={14}/> Export Excel</button>
          {canEdit('gst')&&<button className="btn btn-secondary" onClick={()=>setShowImport(true)}><Upload size={14}/> Import Excel</button>}
          {canEdit('gst')&&<button className="btn btn-primary" onClick={openNew}><Plus size={14}/> Add Bill</button>}
        </div>
      </div>

      {/* Bulk bar */}
      {selected.size>0&&canEdit('gst')&&(
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',background:'var(--accent-light)',border:'1.5px solid var(--accent)',borderRadius:8,padding:'8px 12px',marginBottom:10}}>
          <strong style={{fontSize:13}}>{selected.size} selected</strong>
          {selected.size < filtered.length && <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(new Set(filtered.map(r=>r.id)))}>Select all {filtered.length} filtered</button>}
          <button className="btn btn-secondary btn-sm" onClick={()=>bulkSetStatus('GST Filed',[...selected])}>✅ Filed</button>
          <button className="btn btn-secondary btn-sm" onClick={()=>bulkSetStatus('GST Not Filed',[...selected])}>❌ Not Filed</button>
          <button className="btn btn-secondary btn-sm" onClick={()=>bulkSetStatus('GST Pending',[...selected])}>⏳ Pending</button>
          <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={bulkDelete}><Trash2 size={13}/> Delete</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(new Set())}>Clear selection</button>
        </div>
      )}

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {canEdit('gst')&&<th><input type="checkbox" checked={allPageSelected} onChange={togglePage}/></th>}
              <th>#</th><th>Category</th>
              {thSort('date','Date')}
              <th>GST Number</th>
              {thSort('party',K.partyLabel+' Name')}
              {thSort('invoice','Invoice Number')}
              {thSort('taxable','Taxable (₹)',{textAlign:'right'})}
              <th style={{textAlign:'center'}}>Rate</th>
              <th style={{textAlign:'right'}}>Tax (₹)</th>
              {thSort('total','Invoice Value (₹)',{textAlign:'right'})}
              {thSort('balance','Payment',{textAlign:'right'})}
              <th>Head</th>
              <th>GST Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 ? (
              <tr><td colSpan={colCount}><div className="empty-state"><FileText size={32}/><h3>No bills found</h3></div></td></tr>
            ) : pageRows.map((r,i)=>{
              const st = billStatus(r), neg = signOf(r) < 0;
              return (
              <tr key={r.id} style={{background:selected.has(r.id)?'rgba(59,130,246,0.07)':undefined}}>
                {canEdit('gst')&&<td><input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggleOne(r.id)}/></td>}
                <td style={{fontSize:11,color:'var(--text3)'}}>{(perPage===0?0:(page-1)*perPage)+i+1}</td>
                <td><span className={`badge ${r.category==='B2B'?'badge-blue':'badge-cyan'}`} style={{fontSize:10}}>{r.category}</span></td>
                <td style={{fontSize:12,whiteSpace:'nowrap'}}>{fmtDMY(r.date)}</td>
                <td style={{fontSize:11,fontFamily:'monospace'}}>{r.gst_number||'—'}</td>
                <td style={{fontWeight:500,fontSize:13,minWidth:150}}>{r[P]}</td>
                <td style={{fontSize:12,fontFamily:'monospace',fontWeight:600}}>
                  {r.invoice_number}
                  {r.doc_type&&r.doc_type!=='Invoice'&&<span className="badge badge-purple" style={{fontSize:9,marginLeft:4}}>{r.doc_type==='Credit Note'?'CN':'DN'}</span>}
                  {r.attachment_path&&<button title="Open attached bill" onClick={()=>openBillFile(r.attachment_path)} style={{background:'none',border:'none',cursor:'pointer',padding:'0 2px'}}><Paperclip size={12}/></button>}
                </td>
                <td style={{textAlign:'right',fontSize:13,fontWeight:600,color:neg?'var(--red)':undefined}}>{neg?'−':''}₹{INR(r.taxable_value)}</td>
                <td style={{textAlign:'center'}}><span style={{fontWeight:700,fontSize:12,color:'var(--amber)',background:'#fef3c7',borderRadius:4,padding:'2px 6px'}}>{ratePct(r)}</span></td>
                <td style={{textAlign:'right',color:'var(--amber)',fontSize:13,fontWeight:600}}>{neg?'−':''}₹{INR(r.tax_amount)}</td>
                <td style={{textAlign:'right',color:neg?'var(--red)':'var(--green)',fontSize:14,fontWeight:700}}>{neg?'−':''}₹{INR(r.invoice_value)}</td>
                <td style={{textAlign:'right',whiteSpace:'nowrap'}}>
                  <span className={`badge ${st.cls}`} style={{fontSize:9}}>{st.label}</span>
                  {st.balance>0&&<div style={{fontSize:10,color:'var(--red)'}}>Bal ₹{INR(st.balance)}</div>}
                </td>
                <td style={{fontSize:11,maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.head}>{r.head||'—'}</td>
                <td>
                  <span className={`badge ${r.remarks==='GST Filed'?'badge-green':r.remarks==='GST Not Filed'?'badge-red':'badge-amber'}`} style={{fontSize:9}}>{r.remarks||'—'}</span>
                  {K.itc&&<div style={{marginTop:2,whiteSpace:'nowrap'}}>
                    <span className={`badge ${itcOf(r)==='Eligible'?'badge-green':itcOf(r)==='Pending'?'badge-amber':'badge-red'}`} style={{fontSize:8}}>ITC {itcOf(r)}</span>
                    {r.gstr2b_matched&&<span className="badge badge-blue" style={{fontSize:8,marginLeft:2}}>2B ✓</span>}
                    {r.reverse_charge&&<span className="badge badge-purple" style={{fontSize:8,marginLeft:2}}>RCM</span>}
                  </div>}
                </td>
                <td>
                  <div style={{display:'flex',gap:3}}>
                    <button className="btn btn-ghost btn-sm" title="View" onClick={()=>setShowView(r)}><Eye size={13}/></button>
                    {canEdit('gst')&&<button className="btn btn-ghost btn-sm" title="Payments" onClick={()=>setPayBill(r)}><CreditCard size={13}/></button>}
                    {canEdit('gst')&&<button className="btn btn-ghost btn-sm" title="Edit" onClick={()=>openEdit(r)}><Edit2 size={13}/></button>}
                    {canEdit('gst')&&<button className="btn btn-ghost btn-sm" title="Delete" style={{color:'var(--red)'}} onClick={()=>deleteOne(r)}><Trash2 size={13}/></button>}
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
          {filtered.length>0&&(
            <tfoot>
              <tr style={{background:'var(--bg3)',fontWeight:700}}>
                <td colSpan={(canEdit('gst')?1:0)+6} style={{textAlign:'right',padding:'8px 12px'}}>TOTAL ({filtered.length} bills, credit notes deducted)</td>
                <td style={{textAlign:'right',fontSize:13}}>₹{INR(totTaxable)}</td>
                <td></td>
                <td style={{textAlign:'right',color:'var(--amber)',fontSize:13}}>₹{INR(totTax)}</td>
                <td style={{textAlign:'right',color:'var(--green)',fontSize:14}}>₹{INR(totInvoice)}</td>
                <td style={{textAlign:'right',color:'var(--red)',fontSize:12}}>Bal ₹{INR(totOutstanding)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {filtered.length>0&&(
        <div style={{display:'flex',gap:8,alignItems:'center',justifyContent:'space-between',marginTop:10,flexWrap:'wrap'}}>
          <div style={{fontSize:12,color:'var(--text3)'}}>
            Rows per page:{' '}
            <select value={perPage} onChange={e=>setPerPage(Number(e.target.value))} style={{fontSize:12,padding:'2px 6px',width:80}}>
              {[25,50,100,200].map(n=><option key={n} value={n}>{n}</option>)}<option value={0}>All</option>
            </select>
          </div>
          {perPage!==0&&<div style={{display:'flex',gap:6,alignItems:'center'}}>
            <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(1)}>«</button>
            <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹ Prev</button>
            <span style={{fontSize:12}}>Page {page} of {pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next ›</button>
            <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={()=>setPage(pages)}>»</button>
          </div>}
        </div>
      )}

      {showModal && renderForm()}
      {showView && renderView()}
      {payBill && renderPayments()}
      {showImport && renderImport()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// GST SUMMARY (month-wise liability, rate-wise, outstanding)
// ─────────────────────────────────────────────────────────────────
function GSTSummary() {
  const [fy, setFy] = useState(currentFY());
  const [sale, setSale] = useState([]);
  const [pur, setPur]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [only2b, setOnly2b] = useState(false);

  useEffect(() => { (async () => {
    setLoading(true);
    const { from, to } = fyRange(fy);
    try {
      const q = t => fetchAllPaged(() => supabase.from(t).select('*').gte('date', from).lte('date', to).order('date').order('id'));
      const [s, p] = await Promise.all([q('gst_sale_bills').catch(()=>[]), q('gst_purchase_bills').catch(()=>[])]);
      setSale(s); setPur(p);
    } catch (e) { toast.error('Could not load summary: ' + e.message); }
    setLoading(false);
  })(); }, [fy]);

  const months = fyMonths(fy);
  const calc = useMemo(() => {
    const rows = months.map(ym => {
      const S = sale.filter(r => (r.date||'').startsWith(ym)), Pu = pur.filter(r => (r.date||'').startsWith(ym));
      const sg = f => S.reduce((a,r)=>a+signOf(r)*f(r),0);
      const taxable = sg(r=>Number(r.taxable_value||0));
      const cgst = sg(r=>splitOf(r).cgst), sgst = sg(r=>splitOf(r).sgst), igst = sg(r=>splitOf(r).igst);
      const output = cgst + sgst + igst;
      const rcm = Pu.filter(r=>r.reverse_charge).reduce((a,r)=>a+signOf(r)*Number(r.tax_amount||0),0);
      const claimable = Pu.filter(r=>itcOf(r)==='Eligible' && (!only2b || r.gstr2b_matched)).reduce((a,r)=>a+signOf(r)*Number(r.tax_amount||0),0);
      const notClaimed = Pu.filter(r=>!(itcOf(r)==='Eligible' && (!only2b || r.gstr2b_matched))).reduce((a,r)=>a+signOf(r)*Number(r.tax_amount||0),0);
      const filedS = S.filter(r=>r.remarks==='GST Filed').length, filedP = Pu.filter(r=>r.remarks==='GST Filed').length;
      return { ym, taxable, cgst, sgst, igst, output, rcm, claimable, notClaimed, net: output + rcm - claimable, nS: S.length, filedS, nP: Pu.length, filedP,
               purTaxable: Pu.reduce((a,r)=>a+signOf(r)*Number(r.taxable_value||0),0) };
    });
    const tot = rows.reduce((a,r)=>{ Object.keys(r).forEach(k=>{ if (k!=='ym') a[k]=(a[k]||0)+r[k]; }); return a; }, {});
    const rates = [...new Set([...TAX_RATES, ...sale.map(r=>Number(r.tax_rate)), ...pur.map(r=>Number(r.tax_rate))].filter(x=>!isNaN(x)))].sort((a,b)=>a-b);
    const rateRows = rates.map(rt => {
      const S = sale.filter(r=>Number(r.tax_rate)===rt), Pu = pur.filter(r=>Number(r.tax_rate)===rt);
      const g = (L,f)=>L.reduce((a,r)=>a+signOf(r)*Number(f(r)||0),0);
      return { rt, sT:g(S,r=>r.taxable_value), sX:g(S,r=>r.tax_amount), pT:g(Pu,r=>r.taxable_value), pX:g(Pu,r=>r.tax_amount), n:S.length+Pu.length };
    }).filter(r=>r.n>0);
    const outstanding = (L, key) => {
      const m = {}; L.forEach(r => { const b = billStatus(r).balance; if (b > 0) m[r[key]||'—'] = (m[r[key]||'—']||0) + b; });
      const list = Object.entries(m).sort((a,b)=>b[1]-a[1]);
      return { total: list.reduce((a,x)=>a+x[1],0), top: list.slice(0,10) };
    };
    return { rows, tot, rateRows, recv: outstanding(sale,'party_name'), pay: outstanding(pur,'supplier_name') };
  }, [sale, pur, fy, only2b]);

  function exportSummary() {
    const monthly = calc.rows.map(r => ({ 'Month':monthLabel(r.ym), 'Sales Taxable':r.taxable, 'CGST':r.cgst, 'SGST':r.sgst, 'IGST':r.igst, 'Output Tax':r.output, 'RCM Tax':r.rcm, 'ITC Claimable':r.claimable, 'Net Payable (+) / Credit (−)':r.net, 'Sales Filed':`${r.filedS}/${r.nS}`, 'Purchases Filed':`${r.filedP}/${r.nP}` }));
    monthly.push({ 'Month':'TOTAL', 'Sales Taxable':calc.tot.taxable, 'CGST':calc.tot.cgst, 'SGST':calc.tot.sgst, 'IGST':calc.tot.igst, 'Output Tax':calc.tot.output, 'RCM Tax':calc.tot.rcm, 'ITC Claimable':calc.tot.claimable, 'Net Payable (+) / Credit (−)':calc.tot.net });
    const rate = calc.rateRows.map(r => ({ 'Rate':`${(r.rt*100).toFixed(0)}%`, 'Sales Taxable':r.sT, 'Sales Tax':r.sX, 'Purchase Taxable':r.pT, 'Purchase Tax':r.pX }));
    const w = o => Object.keys(o[0]).reduce((a,k)=>({...a,[k]:k==='Month'?14:16}),{});
    saveStyledXlsx(`GST_Summary_FY${fyLabel(fy)}.xlsx`, [
      { name:'Monthly', title:`GST Summary — FY ${fyLabel(fy)}`, rows:monthly, widths:w(monthly) },
      ...(rate.length ? [{ name:'Rate-wise', title:`Rate-wise — FY ${fyLabel(fy)}`, rows:rate, widths:w(rate) }] : []),
    ]);
    toast.success('Exported ✅');
  }

  const money = v => <span style={{color:v<0?'var(--red)':undefined}}>{v<0?'−':''}₹{INR(Math.abs(v))}</span>;
  const th = {textAlign:'right'};
  return (
    <div>
      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:14}}>
        <label style={{fontSize:12,color:'var(--text3)'}}>Financial year</label>
        <select value={fy} onChange={e=>setFy(e.target.value)} style={{width:130,fontSize:13}}>{fyOptions().map(y=><option key={y} value={y}>FY {fyLabel(y)}</option>)}</select>
        <label style={{display:'flex',gap:6,alignItems:'center',fontSize:12}}><input type="checkbox" checked={only2b} onChange={e=>setOnly2b(e.target.checked)}/> Count only ITC that appears in GSTR-2B</label>
        <button className="btn btn-secondary" onClick={exportSummary} disabled={loading}><Download size={14}/> Export Excel</button>
        {loading&&<span style={{fontSize:12,color:'var(--text3)'}}>Loading…</span>}
      </div>

      <div className="stats-grid" style={{marginBottom:16}}>
        {[
          ['Output tax (sales)', calc.tot.output||0, 'var(--amber)'],
          ['ITC claimable', calc.tot.claimable||0, 'var(--green)'],
          ['Net GST payable (+) / credit (−)', calc.tot.net||0, (calc.tot.net||0)>0?'var(--red)':'var(--green)'],
          ['Receivable (unpaid sales)', calc.recv.total, 'var(--accent)'],
          ['Payable (unpaid purchases)', calc.pay.total, 'var(--text2)'],
        ].map(([l,v,c])=>(
          <div key={l} className="stat-card"><div className="stat-label">{l}</div><div className="stat-value" style={{color:c,fontSize:15}}>{money(v)}</div></div>
        ))}
      </div>

      <div style={{fontWeight:700,fontSize:13,margin:'6px 0'}}>Month-wise GST liability — FY {fyLabel(fy)}</div>
      <div className="table-wrap">
        <table>
          <thead><tr>
            <th>Month</th><th style={th}>Sales taxable</th><th style={th}>CGST</th><th style={th}>SGST</th><th style={th}>IGST</th>
            <th style={th}>Output tax</th><th style={th}>RCM tax</th><th style={th}>ITC claimable</th><th style={th}>Net payable / (credit)</th><th>Filed (sale · purchase)</th>
          </tr></thead>
          <tbody>
            {calc.rows.map(r=>(
              <tr key={r.ym} style={{opacity:(r.nS+r.nP)?1:0.45}}>
                <td style={{fontWeight:600}}>{monthLabel(r.ym)}</td>
                <td style={th}>{money(r.taxable)}</td><td style={th}>{money(r.cgst)}</td><td style={th}>{money(r.sgst)}</td><td style={th}>{money(r.igst)}</td>
                <td style={{...th,fontWeight:600,color:'var(--amber)'}}>{money(r.output)}</td>
                <td style={th}>{money(r.rcm)}</td>
                <td style={{...th,color:'var(--green)'}}>{money(r.claimable)}</td>
                <td style={{...th,fontWeight:700}}>{money(r.net)}</td>
                <td style={{fontSize:12}}>
                  <span style={{color:r.nS&&r.filedS<r.nS?'var(--red)':undefined}}>{r.filedS}/{r.nS}</span> · <span style={{color:r.nP&&r.filedP<r.nP?'var(--red)':undefined}}>{r.filedP}/{r.nP}</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr style={{background:'var(--bg3)',fontWeight:700}}>
            <td>TOTAL</td><td style={th}>{money(calc.tot.taxable||0)}</td><td style={th}>{money(calc.tot.cgst||0)}</td><td style={th}>{money(calc.tot.sgst||0)}</td><td style={th}>{money(calc.tot.igst||0)}</td>
            <td style={th}>{money(calc.tot.output||0)}</td><td style={th}>{money(calc.tot.rcm||0)}</td><td style={th}>{money(calc.tot.claimable||0)}</td><td style={th}>{money(calc.tot.net||0)}</td><td></td>
          </tr></tfoot>
        </table>
      </div>
      <div style={{fontSize:11,color:'var(--text3)',marginTop:6}}>
        Net payable = output tax + reverse-charge tax − ITC claimable. Credit notes are deducted. ITC counts only purchases marked "Eligible"{only2b?' and present in GSTR-2B':''}. Reverse-charge tax is added to the liability and, if eligible, claimed back as ITC.
      </div>

      <div style={{fontWeight:700,fontSize:13,margin:'18px 0 6px'}}>Rate-wise</div>
      <div className="table-wrap" style={{maxWidth:760}}>
        <table>
          <thead><tr><th>Rate</th><th style={th}>Sales taxable</th><th style={th}>Sales tax</th><th style={th}>Purchase taxable</th><th style={th}>Purchase tax</th></tr></thead>
          <tbody>
            {calc.rateRows.length===0 ? <tr><td colSpan={5} style={{textAlign:'center',color:'var(--text3)'}}>No bills in this financial year</td></tr> :
              calc.rateRows.map(r=><tr key={r.rt}><td><strong>{(r.rt*100).toFixed(0)}%</strong></td><td style={th}>{money(r.sT)}</td><td style={th}>{money(r.sX)}</td><td style={th}>{money(r.pT)}</td><td style={th}>{money(r.pX)}</td></tr>)}
          </tbody>
        </table>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))',gap:16,marginTop:18}}>
        {[['Top receivables (customers who owe you)', calc.recv], ['Top payables (suppliers you owe)', calc.pay]].map(([title, o])=>(
          <div key={title}>
            <div style={{fontWeight:700,fontSize:13,margin:'0 0 6px'}}>{title}</div>
            <div className="table-wrap"><table><tbody>
              {o.top.length===0 ? <tr><td style={{color:'var(--text3)',textAlign:'center'}}>Nothing outstanding 🎉</td></tr> :
                o.top.map(([n,v])=><tr key={n}><td>{n}</td><td style={{...th,color:'var(--red)',fontWeight:600}}>₹{INR(v)}</td></tr>)}
            </tbody></table></div>
          </div>
        ))}
      </div>
    </div>
  );
}
